import { useMemo } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { SALES_QUOTES_PAGED_URL } from "../../data/useSalesQuotes";
import { ITableSalesQuote, SalesQuoteStatus } from "../../models/SalesQuote";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import { SearchAccount } from "../accounts/SearchAccount";
import AgeCell from "../shared/Cells/AgeCell";
import ClickToCopy from "../shared/ClickToCopy";
import SelectInput from "../shared/Inputs/SelectInput";
import PagedTable from "../shared/Tables/PagedTable";
import Avatar from "../user/Avatar";
import { SalesQuoteStatusPill } from "./SalesQuotePills";

const ALL = "all";

export default function SalesQuoteList() {
  const navigate = useNavigate();
  const { register, setValue, watch } = useForm({
    defaultValues: {
      status: String(SalesQuoteStatus.Open),
      accountId: undefined as string | undefined,
    },
  });

  const status = watch("status");
  const accountId = watch("accountId");

  // The API filters by exact match on these names; "all" just leaves status off.
  const additionalBody = useMemo(
    () => ({
      status: status === ALL ? undefined : Number(status),
      accountId: accountId || undefined,
    }),
    [status, accountId]
  );

  const colHelper = createColumnHelper<ITableSalesQuote>();

  const cols = useMemo(
    () => [
      colHelper.accessor("created", {
        header: "Age",
        sortingFn: "auto",
        cell: (info) => (
          <div className="flex flex-col gap-0.5">
            <AgeCell created={info.getValue()} />
            <span className="text-xs text-gray-500">{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT)}</span>
          </div>
        ),
      }),
      colHelper.accessor("contactName", {
        header: "Contact",
        sortingFn: "auto",
        cell: (info) => {
          const quote = info.row.original;
          return (
            <div className="flex flex-col text-xs gap-0.5">
              <span className="font-semibold text-gray-900">{quote.contactName || <span className="text-gray-400 italic">No name</span>}</span>
              {quote.contactPhone && <ClickToCopy text={quote.contactPhone} />}
              {quote.contactEmail && <ClickToCopy text={quote.contactEmail} />}
            </div>
          );
        },
      }),
      colHelper.accessor("accountName", {
        header: "Account",
        enableSorting: false,
        cell: (info) => info.getValue() ?? <span className="text-gray-400">—</span>,
      }),
      colHelper.accessor("itemCount", {
        header: "Items",
        enableSorting: false,
      }),
      colHelper.accessor("quotedTotal", {
        header: "Total",
        sortingFn: "auto",
        cell: (info) => <span className="font-semibold text-gray-900">{PriceFormat(info.getValue())}</span>,
      }),
      colHelper.accessor("status", {
        header: "Status",
        sortingFn: "auto",
        cell: (info) => <SalesQuoteStatusPill status={info.getValue()} />,
      }),
      colHelper.accessor("createdById", {
        header: "Created By",
        enableSorting: false,
        cell: (info) => <Avatar userId={info.getValue()} classes="h-8 w-8" />,
      }),
    ],
    []
  );

  return (
    <>
      {/* ---------- Filter ---------- */}
      <form className="mt-2 mx-4 grid grid-cols-3 gap-4" onSubmit={(e) => e.preventDefault()}>
        <div className="col-span-2">
          <SearchAccount
            label="Account"
            onAccountSelect={(account) => setValue("accountId", account?.id)}
          />
        </div>
        <SelectInput
          label="Status"
          options={[
            { id: ALL, name: "All" },
            { id: String(SalesQuoteStatus.Open), name: "Open" },
            { id: String(SalesQuoteStatus.Converted), name: "Converted" },
            { id: String(SalesQuoteStatus.Cancelled), name: "Cancelled" },
          ]}
          register={register}
          registerName="status"
          setValue={setValue}
          watch={watch}
        />
      </form>
      <hr className="text-gray-300 mt-8 col-span-3 mx-4" />
      {/* ---------------------------- */}
      <div className="p-4">
        <PagedTable<ITableSalesQuote>
          url={SALES_QUOTES_PAGED_URL}
          columns={cols}
          queryLabel="Search contact name, email, phone or account"
          onRowClick={(quote: ITableSalesQuote) => navigate(`/sales-quotes/${quote.id}`)}
          showTotal={true}
          additionalBody={additionalBody}
        />
      </div>
    </>
  );
}
